import React from 'react';
import { useSelector } from 'react-redux';
import { userData } from 'selectors/user';
import { isPosting as isPostingSelector } from 'selectors/bookings';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CheckIcon from '@material-ui/icons/Check';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Checkbox, Input } from 'components/FormInputs';
import KlarnaImg from 'assets/img/klarna.png';
import Swish from 'assets/img/swish.png';
import { SvgIcon } from '@material-ui/core';
import AcceptanceTermsText from './acceptance-terms-text';

const CreditCardIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
  </SvgIcon>
);

const RadioInputPaymentMethod = ({ disabled, value, onChange, enableKlarna }) => {
  return (
    <FormControl component="fieldset" style={{ position: 'relative' }}>
      <FormLabel component="legend" style={{ marginBottom: '0.2rem' }}>
        <FormattedMessage id="select.your.paymentmethod" defaultMessage="Select payment method" />
      </FormLabel>
      <RadioGroup
        row
        name="payment_method"
        value={value}
        onChange={onChange}
        style={{ alignItems: 'center' }}
      >
        <FormControlLabel
          value="CARD"
          control={<Radio color="primary" />}
          label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CreditCardIcon style={{ fontSize: 30, marginRight: 8 }} />
              <span style={{ marginLeft: '-3px', display: 'flex', alignItems: 'center' }}>
                <FormattedMessage id="select.paybycard" defaultMessage="Pay by card" />
              </span>
            </div>
          }
          disabled={disabled}
        />

        {enableKlarna && (
          <FormControlLabel
            value="KLARNA"
            control={<Radio color="primary" />}
            label={
              <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <img
                    alt="Klarna"
                    src={KlarnaImg}
                    style={{
                      width: 70,
                      marginLeft: -10,
                      verticalAlign: 'middle',
                      zIndex: 2,
                    }}
                  />
                </div>
                <span style={{ marginLeft: '-10px', display: 'flex', alignItems: 'center' }}>
                  <FormattedMessage id="select.paywithklarna" defaultMessage="Pay with Klarna" />
                </span>
                <img
                  alt="Swish"
                  src={Swish}
                  style={{
                    width: 50,
                    marginLeft: -5,
                    marginTop: 5,
                    position: 'absolute',
                    top: 20,
                    left: 0,
                    zIndex: 1,
                    pointerEvents: 'none',
                  }}
                />
              </div>
            }
            disabled={disabled}
          />
        )}
      </RadioGroup>
    </FormControl>
  );
};

const PayByCard = ({ disabled, value, onChange }) => (
  <FormControl fullWidth>
    <FormLabel component="legend" style={{ marginBottom: '0.2rem', marginTop: '2rem' }}>
      <FormattedMessage id="select.your.creditcard" defaultMessage="Select your card" />
    </FormLabel>
    <RadioGroup row name="card" value={value} onChange={onChange} style={{ alignItems: 'center' }}>
      <FormControlLabel
        value="CB_VISA_MASTERCARD"
        control={<Radio color="primary" />}
        label="Visa, Mastercard, CB"
        disabled={disabled}
      />
      <FormControlLabel
        value="AMEX"
        control={<Radio color="primary" />}
        label="American Express"
        disabled={disabled}
      />
    </RadioGroup>
  </FormControl>
);

const PayByKlarna = ({
  disabled,
  klarna_addressline1,
  klarna_city,
  klarna_postalcode,
  onKlarnaAddressChange,
  onKlarnaCityChange,
  onKlarnaPostalCodeChange,
}) => (
  <Box>
    <FormControl fullWidth>
      <FormLabel component="legend" style={{ marginBottom: '0.8rem', marginTop: '2rem' }}>
        <FormattedMessage id="input.klarna.details" defaultMessage="Enter address details" />
      </FormLabel>
      <FormLabel style={{ marginBottom: '0.2rem', marginTop: '0.5rem' }}>
        <FormattedMessage id="klarna.address" defaultMessage="Street address" />
      </FormLabel>
      <Input
        type="text"
        name="klarna_addressline1"
        value={klarna_addressline1}
        onChange={(e) => onKlarnaAddressChange(e.target.value)}
        disabled={disabled}
        inputProps={{ maxLength: 50 }}
        style={{ width: '250px', marginBottom: '1.2rem' }}
      />
    </FormControl>
    <FormControl fullWidth>
      <FormLabel style={{ marginBottom: '0.2rem', marginTop: '0.5rem' }}>
        <FormattedMessage id="klarna.city" defaultMessage="City" />
      </FormLabel>
      <Input
        type="text"
        name="klarna_city"
        value={klarna_city}
        onChange={(e) => onKlarnaCityChange(e.target.value)}
        disabled={disabled}
        inputProps={{ maxLength: 50 }}
        style={{ width: '250px', marginBottom: '1.2rem' }}
      />
    </FormControl>
    <FormControl fullWidth>
      <FormLabel style={{ marginBottom: '0.2rem', marginTop: '0.5rem' }}>
        <FormattedMessage id="klarna.postalcode" defaultMessage="Postal code" />
      </FormLabel>
      <Input
        type="text"
        name="klarna_postalcode"
        value={klarna_postalcode}
        onChange={(e) => {
          const validValue = e.target.value.replace(/[^0-9\s-]/g, '');
          onKlarnaPostalCodeChange(validValue);
        }}
        disabled={disabled}
        inputProps={{ maxLength: 7 }}
        style={{ width: '250px', marginBottom: '1.2rem' }}
      />
    </FormControl>
  </Box>
);

RadioInputPaymentMethod.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  enableKlarna: PropTypes.bool,
};
PayByCard.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
};
PayByKlarna.propTypes = {
  disabled: PropTypes.bool,
  klarna_addressline1: PropTypes.string,
  klarna_city: PropTypes.string,
  klarna_postalcode: PropTypes.string,
  onKlarnaAddressChange: PropTypes.func,
  onKlarnaCityChange: PropTypes.func,
  onKlarnaPostalCodeChange: PropTypes.func,
};

const IsGuestBookingPending = ({
  booking,
  offerChanged,
  payinLocked,
  onClearOfferChanged,
  onAccept,
}) => {
  const [terms, setTerms] = React.useState(false);
  const [card, setCard] = React.useState('CB_VISA_MASTERCARD');
  const [payment_method, setPaymentMethod] = React.useState('');
  const [klarna_addressline1, setKlarnaAddress] = React.useState('');
  const [klarna_city, setKlarnaCity] = React.useState('');
  const [klarna_postalcode, setKlarnaPostalCode] = React.useState('');

  const user = useSelector(userData);
  const isPosting = useSelector(isPostingSelector);
  const enableKlarna = user.country_of_residence === 'SE';

  React.useEffect(() => {
    if (offerChanged) {
      setTerms(false);
      setPaymentMethod('');
      setCard('CB_VISA_MASTERCARD');
      setKlarnaAddress('');
      setKlarnaCity('');
      setKlarnaPostalCode('');
    }
  }, [offerChanged]);

  const isButtonDisabled =
    isPosting ||
    !terms ||
    !payment_method ||
    (payment_method === 'CARD' && !card) ||
    (payment_method === 'KLARNA' && (!klarna_addressline1 || !klarna_postalcode || !klarna_city));

  // Guards a fast double-click before redux isPosting propagates into the
  // disabled prop. Cleared once the global posting state goes back to false.
  const submittingRef = React.useRef(false);
  React.useEffect(() => {
    if (!isPosting) submittingRef.current = false;
  }, [isPosting]);

  const handleAccept = () => {
    if (submittingRef.current || isPosting) return;
    submittingRef.current = true;
    if (onClearOfferChanged) onClearOfferChanged();
    onAccept(payment_method, card, klarna_addressline1, klarna_city, klarna_postalcode);
  };

  return (
    <Box>
      <Box pl={1.5}>
        {(payinLocked || offerChanged) && (
          <Box
            role="alert"
            display="inline-flex"
            mb={2}
            p={1.5}
            style={{
              backgroundColor: '#fff8e1',
              border: '1px solid #ffd54f',
              borderRadius: 4,
            }}
          >
            {/* The pay-in lock also bumps the version, so a second tab gets the
                same conflict as a host edit; don't blame the host for it. */}
            {payinLocked ? (
              <FormattedMessage
                id="booking.payin.inProgress"
                defaultMessage="You have already started a payment for this booking, e.g. in another tab. Finish it there instead of paying again. If you cancelled that payment, you can try again."
              />
            ) : (
              <FormattedMessage
                id="booking.offer.updated"
                defaultMessage="The host has updated this offer. Please review the new details and accept again to pay."
              />
            )}
          </Box>
        )}
        <Box mb={2}>
          <Checkbox
            name="terms"
            value={terms}
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            style={{ paddingLeft: 0 }}
            label={<AcceptanceTermsText booking={booking} />}
          />
          &nbsp;
        </Box>
        <Box>
          <RadioInputPaymentMethod
            disabled={!terms || isPosting}
            value={payment_method}
            onChange={(e) => {
              setPaymentMethod(e.target.value);
              if (e.target.value !== 'CARD') {
                setCard('');
              } else {
                setCard('CB_VISA_MASTERCARD');
              }
            }}
            enableKlarna={enableKlarna}
          />
        </Box>
        {payment_method === 'CARD' && (
          <PayByCard
            disabled={!terms || isPosting}
            value={card}
            onChange={(e) => setCard(e.target.value)}
          />
        )}
        {enableKlarna && payment_method === 'KLARNA' && (
          <PayByKlarna
            disabled={!terms || isPosting}
            klarna_addressline1={klarna_addressline1}
            klarna_city={klarna_city}
            klarna_postalcode={klarna_postalcode}
            onKlarnaAddressChange={setKlarnaAddress}
            onKlarnaCityChange={setKlarnaCity}
            onKlarnaPostalCodeChange={setKlarnaPostalCode}
          />
        )}
      </Box>
      <Box mt={2}>
        <Button
          variant="contained"
          color="primary"
          disabled={isButtonDisabled}
          onClick={handleAccept}
          aria-busy={isPosting ? 'true' : 'false'}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {isPosting ? (
            <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
          ) : (
            <CheckIcon style={{ marginRight: 8 }} />
          )}
          <FormattedMessage
            id="Accept booking"
            defaultMessage="Accept booking and pay {price}"
            values={{
              price: (
                <>
                  <FormattedNumber value={booking.total_price / 100} /> SEK
                </>
              ),
            }}
          />
        </Button>
      </Box>
    </Box>
  );
};

IsGuestBookingPending.propTypes = {
  onAccept: PropTypes.func.isRequired,
  offerChanged: PropTypes.bool,
  payinLocked: PropTypes.bool,
  onClearOfferChanged: PropTypes.func,
  booking: PropTypes.shape({
    total_price: PropTypes.number.isRequired,
  }).isRequired,
};

export default IsGuestBookingPending;
